import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, removeToken } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  Trash2,
  Edit2,
  Copy,
  Check,
  Loader2,
  Cookie,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import Sidebar from "../components/Sidebar";

export default function CookiesPage() {
  const [cookies, setCookies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_cookies: 0, total_usage: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCookie, setSelectedCookie] = useState(null);
  const [newCookie, setNewCookie] = useState({ email: "", cookie: "" });
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  
  // Paginação
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 10;
  
  const navigate = useNavigate();

  const fetchCookies = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/balanceador?page=${page}&per_page=${perPage}`);
      setCookies(response.data.data || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      if (error.response?.status === 401) {
        removeToken();
        navigate("/login");
      } else {
        toast.error("Falha ao carregar cookies");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, page]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await axios.get(`${API}/balanceador/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCookies();
  }, [fetchCookies]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleAddCookie = async (e) => {
    e.preventDefault();
    if (!newCookie.email || !newCookie.cookie) {
      toast.error("Email e cookie são obrigatórios");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/balanceador`, newCookie);
      toast.success("Cookie adicionado com sucesso");
      setAddDialogOpen(false);
      setNewCookie({ email: "", cookie: "" });
      setPage(1);
      fetchCookies();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Falha ao adicionar cookie");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCookie = async () => {
    if (!selectedCookie) return;

    try {
      await axios.delete(`${API}/balanceador/${selectedCookie.id}`);
      toast.success("Cookie excluído com sucesso");
      setDeleteDialogOpen(false);
      setSelectedCookie(null);
      fetchCookies();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Falha ao excluir cookie");
    }
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success("Copiado!");
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error("Falha ao copiar");
    }
  };

  const truncateText = (text, maxLength = 40) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Skeleton para linha da tabela
  const TableRowSkeleton = () => (
    <div className="grid grid-cols-12 gap-4 p-4 items-center border-b border-[#111111]">
      <div className="col-span-3">
        <Skeleton className="h-4 w-32 bg-[#222222]" />
      </div>
      <div className="col-span-4">
        <Skeleton className="h-4 w-48 bg-[#222222]" />
      </div>
      <div className="col-span-1 flex justify-center">
        <Skeleton className="h-4 w-8 bg-[#222222]" />
      </div>
      <div className="col-span-2">
        <Skeleton className="h-4 w-24 bg-[#222222]" />
      </div>
      <div className="col-span-2 flex justify-end">
        <Skeleton className="h-8 w-8 bg-[#222222]" />
      </div>
    </div>
  );

  return (
    <div data-testid="cookies-page" className="min-h-screen bg-[#000000] flex">
      <Sidebar activeMenu="cookies" />

      <main className="flex-1 md:ml-56 pb-20 md:pb-0">
        <header className="h-14 md:h-16 border-b border-[#222222] flex items-center justify-between px-4 md:px-8 bg-[#0A0A0A]">
          <h1 className="text-lg md:text-xl font-bold text-[#EDEDED] font-heading">
            Gerenciar Cookies
          </h1>
          
          <div className="flex gap-3">
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-cookie-button"
                  className="bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-sm font-bold font-body"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0A0A] border-[#222222] text-[#EDEDED] w-[calc(100vw-2rem)] max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-[#EDEDED] font-heading text-base md:text-lg">
                    Adicionar Cookie
                  </DialogTitle>
                  <DialogDescription className="text-[#888888] font-body text-sm">
                    Se o email já existir, o cookie será atualizado.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddCookie} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-email" className="text-[#888888] font-body">
                      Email
                    </Label>
                    <Input
                      id="new-email"
                      data-testid="new-cookie-email-input"
                      type="email"
                      value={newCookie.email}
                      onChange={(e) => setNewCookie({ ...newCookie, email: e.target.value })}
                      placeholder="user@example.com"
                      className="bg-[#050505] border-[#333333] text-[#EDEDED] focus:border-[#00FF94] focus:ring-1 focus:ring-[#00FF94] placeholder:text-[#555555] rounded-sm font-mono"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-cookie" className="text-[#888888] font-body">
                      Valor do Cookie
                    </Label>
                    <Input
                      id="new-cookie"
                      data-testid="new-cookie-value-input"
                      type="text"
                      value={newCookie.cookie}
                      onChange={(e) => setNewCookie({ ...newCookie, cookie: e.target.value })}
                      placeholder="cookie_value_here"
                      className="bg-[#050505] border-[#333333] text-[#EDEDED] focus:border-[#00FF94] focus:ring-1 focus:ring-[#00FF94] placeholder:text-[#555555] rounded-sm font-mono"
                      required
                    />
                  </div>
                  <DialogFooter className="flex-col sm:flex-row gap-2">
                    <Button
                      type="submit"
                      data-testid="save-cookie-button"
                      disabled={saving}
                      className="bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-sm font-bold font-body w-full sm:w-auto"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </header>

        <div className="p-4 md:p-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
            <div data-testid="stats-total-cookies" className="bg-[#0A0A0A] border border-[#222222] p-4 md:p-6">
              {statsLoading ? (
                <>
                  <Skeleton className="h-8 md:h-9 w-12 md:w-16 bg-[#222222] mb-2" />
                  <Skeleton className="h-3 md:h-4 w-20 md:w-24 bg-[#222222]" />
                </>
              ) : (
                <>
                  <p className="text-2xl md:text-3xl font-bold font-mono text-[#00FF94]">{stats.total_cookies}</p>
                  <p className="text-xs md:text-sm text-[#888888] font-body mt-1">Total Cookies</p>
                </>
              )}
            </div>
            <div data-testid="stats-total-usage" className="bg-[#0A0A0A] border border-[#222222] p-4 md:p-6">
              {statsLoading ? (
                <>
                  <Skeleton className="h-8 md:h-9 w-12 md:w-16 bg-[#222222] mb-2" />
                  <Skeleton className="h-3 md:h-4 w-20 md:w-24 bg-[#222222]" />
                </>
              ) : (
                <>
                  <p className="text-2xl md:text-3xl font-bold font-mono text-[#00F0FF]">{stats.total_usage}</p>
                  <p className="text-xs md:text-sm text-[#888888] font-body mt-1">Total Usos</p>
                </>
              )}
            </div>
          </div>

          {/* Cookies Table - Desktop */}
          <div data-testid="cookies-table" className="hidden md:block bg-[#0A0A0A] border border-[#222222] overflow-hidden">
            <div className="grid grid-cols-12 gap-4 p-4 border-b border-[#222222]">
              <div className="col-span-3 text-[#888888] text-xs uppercase tracking-wider font-mono">Email</div>
              <div className="col-span-4 text-[#888888] text-xs uppercase tracking-wider font-mono">Cookie</div>
              <div className="col-span-1 text-[#888888] text-xs uppercase tracking-wider font-mono text-center">Usos</div>
              <div className="col-span-2 text-[#888888] text-xs uppercase tracking-wider font-mono">Atualizado</div>
              <div className="col-span-2 text-[#888888] text-xs uppercase tracking-wider font-mono text-right">Ações</div>
            </div>

            {loading ? (
              // Skeleton Loading
              <div>
                {[...Array(5)].map((_, i) => (
                  <TableRowSkeleton key={i} />
                ))}
              </div>
            ) : cookies.length === 0 ? (
              <div data-testid="no-cookies-message" className="p-12 text-center">
                <Cookie className="w-12 h-12 text-[#333333] mx-auto mb-4" />
                <p className="text-[#888888] font-body">Nenhum cookie cadastrado</p>
                <p className="text-[#555555] text-sm mt-2 font-body">Clique em "Adicionar" para começar</p>
              </div>
            ) : (
              <div className="divide-y divide-[#111111]">
                {cookies.map((cookie) => (
                  <div
                    key={cookie.id}
                    data-testid={`cookie-row-${cookie.id}`}
                    className="grid grid-cols-12 gap-4 p-4 hover:bg-[#111111] transition-colors duration-150 items-center"
                  >
                    <div className="col-span-3 font-mono text-sm text-[#EDEDED] truncate" title={cookie.email}>
                      {cookie.email}
                    </div>
                    <div className="col-span-4 flex items-center gap-2">
                      <span className="font-mono text-sm text-[#EDEDED] truncate" title={cookie.cookie}>
                        {truncateText(cookie.cookie)}
                      </span>
                      <button
                        data-testid={`copy-cookie-${cookie.id}`}
                        onClick={() => copyToClipboard(cookie.cookie, cookie.id)}
                        className="text-[#555555] hover:text-[#00FF94] transition-colors"
                      >
                        {copiedId === cookie.id ? (
                          <Check className="w-4 h-4 text-[#00FF94]" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <div className="col-span-1 font-mono text-sm text-[#00F0FF] text-center">{cookie.usage_count}</div>
                    <div className="col-span-2 font-mono text-xs text-[#888888]">{formatDate(cookie.updated_at)}</div>
                    <div className="col-span-2 text-right">
                      <Button
                        data-testid={`delete-cookie-${cookie.id}`}
                        onClick={() => {
                          setSelectedCookie(cookie);
                          setDeleteDialogOpen(true);
                        }}
                        variant="ghost"
                        size="sm"
                        className="text-[#FF3B30] hover:text-white hover:bg-[#FF3B30] rounded-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Paginação */}
            {!loading && totalPages > 1 && (
              <div className="flex flex-col md:flex-row items-center justify-between p-4 border-t border-[#222222] gap-3 md:gap-0">
                <p className="text-xs md:text-sm text-[#888888] font-body">
                  Mostrando {((page - 1) * perPage) + 1} - {Math.min(page * perPage, total)} de {total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    data-testid="prev-page-button"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    variant="ghost"
                    size="sm"
                    className="text-[#888888] hover:text-[#EDEDED] hover:bg-[#111111] disabled:opacity-30 rounded-sm"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-[#EDEDED] font-mono px-2">
                    {page} / {totalPages}
                  </span>
                  <Button
                    data-testid="next-page-button"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    variant="ghost"
                    size="sm"
                    className="text-[#888888] hover:text-[#EDEDED] hover:bg-[#111111] disabled:opacity-30 rounded-sm"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Cookies Cards - Mobile */}
          <div className="md:hidden space-y-3">
            {loading ? (
              [...Array(3)].map((_, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-[#222222] p-4">
                  <Skeleton className="h-4 w-48 bg-[#222222] mb-2" />
                  <Skeleton className="h-3 w-full bg-[#222222] mb-3" />
                  <div className="flex justify-between">
                    <Skeleton className="h-3 w-16 bg-[#222222]" />
                    <Skeleton className="h-8 w-20 bg-[#222222]" />
                  </div>
                </div>
              ))
            ) : cookies.length === 0 ? (
              <div className="bg-[#0A0A0A] border border-[#222222] p-8 text-center">
                <Cookie className="w-10 h-10 text-[#333333] mx-auto mb-3" />
                <p className="text-[#888888] font-body text-sm">Nenhum cookie cadastrado</p>
                <p className="text-[#555555] text-xs mt-2 font-body">Toque em "+" para adicionar</p>
              </div>
            ) : (
              cookies.map((cookie) => (
                <div
                  key={cookie.id}
                  className="bg-[#0A0A0A] border border-[#222222] p-4"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-sm text-[#EDEDED] truncate mb-1">{cookie.email}</p>
                      <div className="flex items-center gap-2">
                        <p className="font-mono text-xs text-[#888888] truncate flex-1">{truncateText(cookie.cookie, 30)}</p>
                        <button
                          onClick={() => copyToClipboard(cookie.cookie, cookie.id)}
                          className="text-[#555555] hover:text-[#00FF94]"
                        >
                          {copiedId === cookie.id ? (
                            <Check className="w-4 h-4 text-[#00FF94]" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-[#111111]">
                    <div className="flex items-center gap-4 text-xs text-[#888888]">
                      <span className="font-mono">Usos: <span className="text-[#00FF94]">{cookie.usage_count}</span></span>
                      <span className="font-mono">{formatDate(cookie.updated_at)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setEditingCookie(cookie);
                          setDialogOpen(true);
                        }}
                        className="text-[#555555] hover:text-[#00F0FF] p-1"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedCookie(cookie);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-[#555555] hover:text-[#FF3B30] p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#0A0A0A] border-[#222222] w-[calc(100vw-2rem)] max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#EDEDED] font-heading text-base md:text-lg">Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription className="text-[#888888] font-body text-sm">
              Tem certeza que deseja excluir o cookie de{" "}
              <span className="text-[#EDEDED] font-mono text-xs md:text-sm break-all">{selectedCookie?.email}</span>? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="bg-[#111111] border-[#333333] text-[#EDEDED] hover:bg-[#222222] rounded-sm font-body w-full sm:w-auto">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-button"
              onClick={handleDeleteCookie}
              className="bg-[#FF3B30] text-white hover:bg-[#D63025] rounded-sm font-body w-full sm:w-auto"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
